'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// UI lib components
import { Avatar, Indicator, Tooltip } from '@mantine/core';

// Context
import useOptionalWorkspace from '@features/Workspace/store/hooks/useOptionalWorkspace';

// Local helpers & utils
import {
  isWorkspaceUserSuspended,
  parseUserName,
} from '@app/shared/helpers/auth';

// Types
import { ActivityStatus } from '@app/types/blackroom-api';
import { UserProps } from './types';

// Style
import * as classes from './styles/index.css';
import * as tooltipStylesAPI from './styles/tooltipStylesAPI.css';
import * as indicatorStylesAPI from './styles/indicatorStylesAPI.css';
import { palette } from '@ui-kit/colors';
import { memo } from 'react';

// Helpers
const getActivityColor = (
  isSuspended: boolean,
  activityStatus: ActivityStatus | undefined,
) => {
  if (isSuspended) {
    return palette.blackroomRed;
  }
  switch (activityStatus) {
    case ActivityStatus.Online:
      return palette.blackroomGreen;
    case ActivityStatus.Idle:
      return palette.blackroomOrange;
    case ActivityStatus.Offline:
    default:
      return palette.blackroomGray;
  }
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function User({
  user,
  fullName: overridingFullName,
  size = 'md',
  withActivityIndicator = false,
  withoutTooltip = false,
  ...props
}: UserProps) {
  /* ********************************** PROPS ********************************* */

  const { email, pictureURL } = user;

  /* ********************************** HOOKS ********************************* */

  // Context
  const workspace = useOptionalWorkspace();

  // Style

  /* ***************************** LOCAL VARIABLES **************************** */

  const { fullName, initials } = parseUserName(user, overridingFullName);
  const fullNameWithEmail = `${fullName} (${email})`;

  const isSuspended = !!workspace && isWorkspaceUserSuspended(user, workspace);

  const activityColor = getActivityColor(isSuspended, user.activityStatus);

  /* ******************************** RENDERING ******************************* */
  return (
    <Tooltip
      classNames={{ ...tooltipStylesAPI }}
      label={
        <>
          {fullName}
          <br />({email})
        </>
      }
      position="top"
      multiline
      withArrow
      withinPortal
      display={withoutTooltip ? 'none' : undefined}
    >
      <Indicator
        position="bottom-end"
        classNames={{ ...indicatorStylesAPI }}
        color={activityColor}
        disabled={!withActivityIndicator}
        withBorder
      >
        <Avatar
          className={classes.user}
          src={pictureURL}
          alt={fullNameWithEmail}
          radius="xl"
          size={size}
          imageProps={{ referrerPolicy: 'no-referrer' }}
          opacity={isSuspended ? 0.3 : undefined}
          {...props}
        >
          {pictureURL ? null : initials}
        </Avatar>
      </Indicator>
    </Tooltip>
  );
}

export default memo(User);
